/**
 * Walk a drag-and-drop payload, keeping the folder tree.
 *
 * `DataTransfer.files` flattens a dropped folder to nothing — the folder itself
 * is not a File, so dropping a tender download onto the drop zone did nothing
 * at all. The `webkitGetAsEntry` API is the only way to see the directory, and
 * it exists in every browser we target despite the vendor prefix.
 *
 * Two things about it are easy to get wrong and are handled here:
 *
 * 1. **`dataTransfer.items` is emptied when the drop handler returns.** The
 *    entries must be collected *synchronously*, before the first `await`, or
 *    the list is empty by the time the walk starts.
 * 2. **`readEntries` returns at most 100 children per call** and signals the end
 *    with an empty batch, so it has to be called in a loop. A single call
 *    silently truncates a folder at 100 files — exactly the kind of partial
 *    success that looks like a complete one.
 */

/** A file plus the directory it sat in, relative to what was dropped. */
export interface DroppedFile {
  file: File;
  /** POSIX-style directory, `""` when the file was dropped at the top level. */
  folderPath: string;
}

/**
 * Ceilings on a dropped tree. A drop is user-controlled and a deep or wide
 * directory would otherwise walk indefinitely — a symlinked parent is a cycle,
 * and the walk has no way to detect one.
 */
export const MAX_DROPPED_FILES = 2000;
export const MAX_DROPPED_DEPTH = 12;

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (cb: (f: File) => void, err: (e: unknown) => void) => void;
  createReader?: () => {
    readEntries: (cb: (entries: FileSystemEntryLike[]) => void, err: (e: unknown) => void) => void;
  };
}

function entryFile(entry: FileSystemEntryLike): Promise<File | null> {
  return new Promise((resolve) => {
    if (!entry.file) return resolve(null);
    entry.file(
      (f) => resolve(f),
      () => resolve(null),
    );
  });
}

function readBatch(reader: {
  readEntries: (cb: (e: FileSystemEntryLike[]) => void, err: (e: unknown) => void) => void;
}): Promise<FileSystemEntryLike[]> {
  return new Promise((resolve) => {
    reader.readEntries(
      (entries) => resolve(entries),
      () => resolve([]),
    );
  });
}

async function walk(
  entry: FileSystemEntryLike,
  folderPath: string,
  depth: number,
  out: DroppedFile[],
): Promise<void> {
  if (out.length >= MAX_DROPPED_FILES) return;

  if (entry.isFile) {
    const file = await entryFile(entry);
    if (file) out.push({ file, folderPath });
    return;
  }
  if (!entry.isDirectory || depth >= MAX_DROPPED_DEPTH) return;

  const reader = entry.createReader?.();
  if (!reader) return;
  const childPath = folderPath ? `${folderPath}/${entry.name}` : entry.name;

  // readEntries yields in batches and signals the end with an empty one.
  for (;;) {
    const batch = await readBatch(reader);
    if (batch.length === 0) break;
    for (const child of batch) {
      await walk(child, childPath, depth + 1, out);
      if (out.length >= MAX_DROPPED_FILES) return;
    }
  }
}

/**
 * Files in a drop, with their folders. Falls back to the flat `files` list when
 * the browser gives no entry API — worse, but never worse than today.
 */
export async function filesFromDataTransfer(dt: DataTransfer): Promise<DroppedFile[]> {
  // Collected synchronously: `dt.items` does not survive the first await.
  const entries: FileSystemEntryLike[] = [];
  const items = dt.items;
  if (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const asEntry = (
        item as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntryLike | null }
      ).webkitGetAsEntry?.();
      if (asEntry) entries.push(asEntry);
    }
  }

  if (entries.length === 0) {
    return Array.from(dt.files).map((file) => ({ file, folderPath: "" }));
  }

  const out: DroppedFile[] = [];
  for (const entry of entries) {
    await walk(entry, "", 0, out);
    if (out.length >= MAX_DROPPED_FILES) break;
  }
  return out;
}

/**
 * The directory part of a picked file's path, `""` at the root.
 *
 * `webkitRelativePath` is populated by a `webkitdirectory` input and is the
 * whole reason a folder pick can keep its tree — it was being read nowhere, so
 * every file arrived looking like it had been dropped loose.
 */
export function folderPathOf(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (!rel) return "";
  const cut = rel.lastIndexOf("/");
  return cut <= 0 ? "" : rel.slice(0, cut);
}

/** Stable identity for a staged file: two same-named files in different folders are different files. */
export function stagedKey(folderPath: string, filename: string): string {
  return folderPath ? `${folderPath}/${filename}` : filename;
}
